/**
 * apiFactory.js
 *
 * Factory dinámica para recursos REST que combina Axios + React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import qs from 'qs'; // para serializar params en queries

/**
 * Serializa params de forma estable (ordenando claves) para usar en queryKey.
 * @param {object|undefined} params
 * @returns {string|undefined}
 */
function stableSerialize(params) {
  if (params === undefined || params === null) return undefined;
  // Usamos qs para evitar discrepancias en orden y para soportar arrays/objets
  try {
    return qs.stringify(params, { arrayFormat: 'brackets', sort: (a, b) => a.localeCompare(b) });
  } catch (e) {
    try {
      // fallback simple
      return JSON.stringify(params, Object.keys(params).sort());
    } catch {
      return String(params);
    }
  }
}

/**
 * Crea los handlers (imperativos + hooks) para un recurso concreto.
 * @param {import('axios').AxiosInstance} axiosInstance - Instancia axios.
 * @param {string} resource - Nombre del recurso (ej: 'departments').
 * @returns {object} Objeto con métodos: getAll, get, post, put, delete, useList, useItem, useCreate, useUpdate, useDelete
 */
function createResourceHandlers(axiosInstance, resource) {
  const baseUrl = `/${resource}`;

  /* ---------------------------
     Métodos imperativos (Promises)
     --------------------------- */

  /**
   * Obtener lista (GET /resource)
   * @param {object} [params] - Query params (paginación, filtros).
   * @param {object} [config] - Axios config adicional.
   * @returns {Promise<any>}
   */
  function getAll(params, config) {
    return axiosInstance.get(baseUrl, { params, ...config }).then((r) => r.data);
  }

  /**
   * Obtener un item por id (GET /resource/:id)
   * @param {string|number} id
   * @param {object} [params]
   * @param {object} [config]
   * @returns {Promise<any>}
   */
  function get(id, params, config) {
    return axiosInstance.get(`${baseUrl}/${id}`, { params, ...config }).then((r) => r.data);
  }

  /**
   * Crear recurso (POST /resource)
   * @param {any} data
   * @param {object} [config]
   * @returns {Promise<any>}
   */
  function post(data, config) {
    return axiosInstance.post(baseUrl, data, config).then((r) => r.data);
  }

  /**
   * Actualizar recurso (PUT /resource/:id)
   * @param {string|number} id
   * @param {any} data
   * @param {object} [config]
   * @returns {Promise<any>}
   */
  function put(id, data, config) {
    return axiosInstance.put(`${baseUrl}/${id}`, data, config).then((r) => r.data);
  }

  /**
   * Eliminar recurso (DELETE /resource/:id)
   * @param {string|number} id
   * @param {object} [config]
   * @returns {Promise<any>}
   */
  function del(id, config) {
    return axiosInstance.delete(`${baseUrl}/${id}`, config).then((r) => r.data);
  }

  /* ---------------------------
     Hooks React Query
     --------------------------- */

  /**
   * Hook para listar recursos (React Query)
   * @param {object} [params] - query params
   * @param {object} [options] - opciones de useQuery ({ staleTime, enabled, ...})
   * @returns {import('@tanstack/react-query').UseQueryResult<any, any>}
   */
  function useList(params, options) {
    const key = [resource, 'list', stableSerialize(params)];
    return useQuery(key, () => getAll(params), options);
  }

  /**
   * Hook para obtener un item por id (React Query)
   * @param {string|number|null|undefined} id
   * @param {object} [params]
   * @param {object} [options]
   * @returns {import('@tanstack/react-query').UseQueryResult<any, any>}
   */
  function useItem(id, params, options) {
    const key = [resource, 'item', id, stableSerialize(params)];
    return useQuery(
      key,
      () => {
        if (id === null || id === undefined) {
          // React Query requiere error o enabled: false; preferimos lanzar para que sea claro si enabled no se maneja
          return Promise.reject(new Error('useItem: id is required'));
        }
        return get(id, params);
      },
      { enabled: id !== undefined && id !== null, ...options }
    );
  }

  /**
   * Hook para crear (POST) con invalidación básica
   * @param {object} [options] - opciones para useMutation
   * @returns {import('@tanstack/react-query').UseMutationResult<any, any, any, any>}
   */
  function useCreate(options) {
    const qc = useQueryClient();
    return useMutation((payload) => post(payload), {
      onSuccess(data, variables, context) {
        // invalidamos listas del recurso
        qc.invalidateQueries([resource, 'list']);
        if (options && typeof options.onSuccess === 'function') options.onSuccess(data, variables, context);
      },
      ...options,
    });
  }

  /**
   * Hook para actualizar (PUT) con invalidaciones
   * Se espera que variables contenga { id, ...payload }
   * @param {object} [options]
   * @returns {import('@tanstack/react-query').UseMutationResult<any, any, any, any>}
   */
  function useUpdate(options) {
    const qc = useQueryClient();
    return useMutation(({ id, ...payload }) => put(id, payload), {
      onSuccess(data, variables, context) {
        qc.invalidateQueries([resource, 'list']);
        if (variables && variables.id) qc.invalidateQueries([resource, 'item', variables.id]);
        if (options && typeof options.onSuccess === 'function') options.onSuccess(data, variables, context);
      },
      ...options,
    });
  }

  /**
   * Hook para eliminar (DELETE) con invalidaciones
   * Se espera variables: { id }
   * @param {object} [options]
   * @returns {import('@tanstack/react-query').UseMutationResult<any, any, any, any>}
   */
  function useDelete(options) {
    const qc = useQueryClient();
    return useMutation(({ id }) => del(id), {
      onSuccess(data, variables, context) {
        qc.invalidateQueries([resource, 'list']);
        if (options && typeof options.onSuccess === 'function') options.onSuccess(data, variables, context);
      },
      ...options,
    });
  }

  return {
    // Imperative
    getAll,
    get,
    post,
    put,
    delete: del,

    // Hooks
    useList,
    useItem,
    useCreate,
    useUpdate,
    useDelete,
  };
}

/**
 * Esto crea la API Factory usando Proxy para generar recursos bajo demanda.
 * Uso:
 *   const api = createApiFactory(axiosInstance);
 *   api.<recurso>.getAll(...)
 *   api.<recurso>.useList(...)
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 * @returns {object} Proxy que resuelve recursos dinámicamente.
 */
export function createApiFactory(axiosInstance) {
  const cache = new Map();

  const handler = {
    get(_target, prop) {
      if (typeof prop !== 'string') return undefined;
      if (!cache.has(prop)) {
        cache.set(prop, createResourceHandlers(axiosInstance, prop));
      }
      return cache.get(prop);
    },
  };

  return new Proxy({}, handler);
}
